import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice, nanoid } from '@reduxjs/toolkit'
import { useSelector } from 'react-redux'

import { normalizeAppPath } from '~/navigation/isInAppPath'
import type { PartialStore } from '~/store/types'

/**
 * In-app tabs for the desktop shell (Linear-style: one webcontents, tabs are
 * client-side navigation contexts). The active tab's `path` tracks the
 * router; switching tabs pushes the stored path. Only mounted/driven by
 * `DesktopTitleBar`, so this state is inert in a browser.
 */

export type DesktopTab = {
  id: string
  path: string
  /** Resolved page title (from document.title); null until first resolution. */
  title: string | null
}

type DesktopTabsState = {
  tabs: DesktopTab[]
  activeTabId: string | null
  /**
   * Paths each tab has occupied this session. Back/Forward prefer in-tab
   * history when the active tab has already been to the landed URL, instead
   * of activating an older tab that happens to sit on the same path
   * (Cmd+T / + always opens `/dashboard`, so duplicates are common).
   */
  visitedPathsByTabId: Record<string, string[]>
}

type TabsRestoredPayload = {
  /** Tabs persisted from the previous session (may be empty). */
  tabs: DesktopTab[]
  /** The route the window actually opened on (deep links win over history). */
  currentPath: string
}

const initialState: DesktopTabsState = {
  tabs: [],
  activeTabId: null,
  visitedPathsByTabId: {}
}

const desktopTabsSlice = createSlice({
  name: 'desktopTabs',
  initialState,
  reducers: {
    /**
     * Hydrate from the previous session, reconciled against the path the
     * window opened on: an existing tab matching it becomes active, otherwise
     * the current page becomes a new tab. Keeps a cold-start deep link from
     * being yanked away by restored state.
     */
    tabsRestored: (state, action: PayloadAction<TabsRestoredPayload>) => {
      const { tabs, currentPath } = action.payload
      const normalizedCurrent = normalizeAppPath(currentPath)
      state.tabs = tabs.map((tab) => ({ ...tab, path: normalizeAppPath(tab.path) }))
      state.visitedPathsByTabId = {}
      for (const tab of state.tabs) rememberVisit(state, tab.id, tab.path)
      const match = state.tabs.find((tab) => tab.path === normalizedCurrent)
      if (match) {
        state.activeTabId = match.id
        return
      }
      const opened: DesktopTab = { id: nanoid(), path: normalizedCurrent, title: null }
      state.tabs.push(opened)
      state.activeTabId = opened.id
      rememberVisit(state, opened.id, opened.path)
    },

    /** Open a route as a new tab (inserted after the active tab) and activate it. */
    tabOpened: {
      prepare: (path: string) => ({ payload: { id: nanoid(), path: normalizeAppPath(path) } }),
      reducer: (state, action: PayloadAction<{ id: string; path: string }>) => {
        const tab: DesktopTab = { id: action.payload.id, path: action.payload.path, title: null }
        const activeIndex = state.tabs.findIndex((candidate) => candidate.id === state.activeTabId)
        state.tabs.splice(activeIndex + 1, 0, tab)
        state.activeTabId = tab.id
        rememberVisit(state, tab.id, tab.path)
      }
    },

    tabActivated: (state, action: PayloadAction<string>) => {
      if (state.tabs.some((tab) => tab.id === action.payload)) {
        state.activeTabId = action.payload
      }
    },

    /** Activate by strip position; `last` overrides index (the Cmd+9 convention). */
    tabAtIndexActivated: (state, action: PayloadAction<{ index: number; last?: boolean }>) => {
      const { index, last } = action.payload
      const target = last ? state.tabs[state.tabs.length - 1] : state.tabs[index]
      if (target) state.activeTabId = target.id
    },

    nextTabActivated: (state) => {
      state.activeTabId = neighborTabId(state, 1)
    },

    previousTabActivated: (state) => {
      state.activeTabId = neighborTabId(state, -1)
    },

    /** Close a tab; when it was active, its right neighbor (or new last) takes over. */
    tabClosed: (state, action: PayloadAction<string>) => {
      if (state.tabs.length <= 1) return
      const index = state.tabs.findIndex((tab) => tab.id === action.payload)
      const closing = state.tabs[index]
      if (!closing) return
      const wasActive = closing.id === state.activeTabId
      state.tabs.splice(index, 1)
      if (state.visitedPathsByTabId) delete state.visitedPathsByTabId[closing.id]
      if (wasActive) {
        const successor = state.tabs[index] ?? state.tabs[state.tabs.length - 1]
        if (successor) state.activeTabId = successor.id
      }
    },

    /** The last tab cannot close from the strip; the shell closes the window instead. */
    activeTabClosed: (state) => {
      if (!state.activeTabId) return
      desktopTabsSlice.caseReducers.tabClosed(state, tabClosed(state.activeTabId))
    },

    /** Keep the active tab's path in sync as the user navigates organically. */
    activeTabNavigated: (state, action: PayloadAction<string>) => {
      const path = normalizeAppPath(action.payload)
      const active = state.tabs.find((tab) => tab.id === state.activeTabId)
      if (!active) {
        const opened: DesktopTab = { id: nanoid(), path, title: null }
        state.tabs.push(opened)
        state.activeTabId = opened.id
        rememberVisit(state, opened.id, opened.path)
        return
      }
      rememberVisit(state, active.id, active.path)
      rememberVisit(state, active.id, path)
      if (active.path !== path) {
        const pathnameChanged = pathWithoutSearch(active.path) !== pathWithoutSearch(path)
        active.path = path
        // Query-only updates (charger ?tab=, list ?mode=) are the same page;
        // dropping the resolved title would flash the fallback for no reason.
        if (pathnameChanged) active.title = null
      }
    },

    /**
     * Back/Forward landed on this path. Tab switches share one history stack,
     * so a traverse often returns to another tab's URL. Activate that tab
     * instead of rewriting the current tab's stored route (and title) — but
     * prefer in-tab history when the active tab already owns this page
     * (same pathname, or a path it occupied earlier). Duplicate `/dashboard`
     * tabs from Cmd+T / + would otherwise steal focus on Back. Pathname
     * matching covers query-only siblings (`?tab=`, `?mode=`).
     */
    historyTraversed: (state, action: PayloadAction<string>) => {
      const landed = normalizeAppPath(action.payload)
      const active = state.tabs.find((tab) => tab.id === state.activeTabId)
      if (active && tabHasInTabHistory(state, active, landed)) {
        desktopTabsSlice.caseReducers.activeTabNavigated(state, activeTabNavigated(landed))
        return
      }
      const match = state.tabs.find((tab) => tabOwnsPath(tab, landed))
      if (match) {
        state.activeTabId = match.id
        if (match.path !== landed) {
          desktopTabsSlice.caseReducers.activeTabNavigated(state, activeTabNavigated(landed))
        }
        return
      }
      desktopTabsSlice.caseReducers.activeTabNavigated(state, activeTabNavigated(landed))
    },

    activeTabTitleChanged: (state, action: PayloadAction<string>) => {
      const active = state.tabs.find((tab) => tab.id === state.activeTabId)
      if (active) active.title = action.payload
    }
  }
})

function pathWithoutSearch(path: string): string {
  const normalized = normalizeAppPath(path)
  const index = normalized.indexOf('?')
  return index === -1 ? normalized : normalized.slice(0, index)
}

function tabOwnsPath(tab: DesktopTab, path: string): boolean {
  const normalized = normalizeAppPath(path)
  return tab.path === normalized || pathWithoutSearch(tab.path) === pathWithoutSearch(normalized)
}

function rememberVisit(state: DesktopTabsState, tabId: string, path: string) {
  if (!state.visitedPathsByTabId) state.visitedPathsByTabId = {}
  const visited = state.visitedPathsByTabId[tabId]
  if (!visited) {
    state.visitedPathsByTabId[tabId] = [path]
    return
  }
  if (!visited.includes(path)) visited.push(path)
}

/** True when Back/Forward is continuing this tab's own history, not a tab switch. */
function tabHasInTabHistory(state: DesktopTabsState, tab: DesktopTab, path: string): boolean {
  if (tabOwnsPath(tab, path)) return true
  const visited = state.visitedPathsByTabId?.[tab.id]
  return Boolean(visited?.includes(path))
}

function neighborTabId(state: DesktopTabsState, offset: number): string | null {
  if (state.tabs.length === 0) return null
  const index = state.tabs.findIndex((tab) => tab.id === state.activeTabId)
  const next = (index + offset + state.tabs.length) % state.tabs.length
  return state.tabs[next]?.id ?? null
}

export const {
  tabsRestored,
  tabOpened,
  tabActivated,
  tabAtIndexActivated,
  nextTabActivated,
  previousTabActivated,
  tabClosed,
  activeTabClosed,
  activeTabNavigated,
  historyTraversed,
  activeTabTitleChanged
} = desktopTabsSlice.actions

export default desktopTabsSlice.reducer

type DesktopTabsStore = PartialStore<typeof desktopTabsSlice>

const selectTabs = (state: DesktopTabsStore) => state.desktopTabs.tabs
const selectActiveTabId = (state: DesktopTabsStore) => state.desktopTabs.activeTabId

export function useDesktopTabs() {
  const tabs = useSelector(selectTabs)
  const activeTabId = useSelector(selectActiveTabId)
  return { tabs, activeTabId }
}
